import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Award, Download, ShieldOff, Loader2, Clock, BookOpen } from "lucide-react";
import { useState } from "react";
import { getMyCertificates } from "@/lib/api/certificates.functions";
import { listMyUpcomingCertificates } from "@/lib/api/academy.functions";
import { downloadCertificatePdf } from "@/lib/certificate-pdf";
import { CertificatePreview } from "@/components/academy/CertificatePreview";

const TRACK_LABEL: Record<string, string> = {
  writing: "Legal Writing Academy",
  research: "Legal Research Hub",
  court_training: "Court Online Training Centre",
  tutoring: "LLB Tutoring Centre",
};

export const Route = createFileRoute("/_authenticated/portal/certificates")({
  component: MyCertificatesPage,
});

function MyCertificatesPage() {
  const fetchCerts = useServerFn(getMyCertificates);
  const fetchUpcoming = useServerFn(listMyUpcomingCertificates);
  const { data, isLoading } = useQuery({
    queryKey: ["my-certificates"],
    queryFn: () => fetchCerts(),
  });
  const upcoming = useQuery({
    queryKey: ["my-upcoming-certs"],
    queryFn: () => fetchUpcoming(),
  });
  const certs = data?.certificates ?? [];
  const upcomingItems = upcoming.data?.items ?? [];
  const recipientName = upcoming.data?.recipientName ?? "Learner";

  return (
    <section className="space-y-10">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Education</p>
        <h2 className="mt-1 font-display text-2xl text-navy">My certificates</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every completion certificate you've earned across the Education Division. Each PDF includes a
          QR that anyone can scan to verify authenticity.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!isLoading && certs.length === 0 && upcomingItems.length === 0 && (
        <div className="rounded-md border border-dashed border-input bg-muted/20 p-8 text-center">
          <Award className="mx-auto h-10 w-10 text-gold" />
          <p className="mt-3 text-sm text-muted-foreground">
            You haven't earned any certificates yet. Complete a course in the Writing Academy, Research Hub,
            or Court Online Training Centre to receive one.
          </p>
        </div>
      )}

      {certs.length > 0 && (
        <div>
          <h3 className="font-display text-lg text-navy">Issued</h3>
          <ul className="mt-3 space-y-3">
            {certs.map((c: any) => (
              <CertRow key={c.id} cert={c} />
            ))}
          </ul>
        </div>
      )}

      {upcomingItems.length > 0 && (
        <div>
          <h3 className="flex items-center gap-2 font-display text-lg text-navy">
            <Clock className="h-4 w-4 text-gold" /> Upcoming
          </h3>
          <p className="text-xs text-muted-foreground">
            Courses you're working through. Preview the exact certificate you'll receive once you pass the
            final exam and an admin signs it off.
          </p>
          <ul className="mt-3 grid gap-4 lg:grid-cols-2">
            {upcomingItems.map((u: any) => (
              <li key={u.progressId} className="rounded-lg border border-input bg-card p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-widest text-gold">
                    {u.course?.code ?? "—"} · {u.course?.category ?? ""}
                  </p>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                    u.status === "awaiting_review" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"
                  }`}>
                    {u.status === "awaiting_review" ? "Awaiting admin sign-off" : "In progress"}
                  </span>
                </div>
                <CertificatePreview
                  recipientName={recipientName}
                  title={`${u.course?.code ?? ""} — ${u.course?.title ?? "Course"}`}
                  trackLabel="NDLCS Academy"
                  template="classic"
                  preview
                />
                <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">
                    {u.status === "awaiting_review"
                      ? "Final exam passed — admin will issue your signed certificate soon."
                      : "Pass every module quiz + final exam to unlock issue."}
                  </span>
                  <Link
                    to="/portal/academy/$slug"
                    params={{ slug: u.courseSlug }}
                    className="inline-flex items-center gap-1 rounded-md border border-navy px-2.5 py-1.5 font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
                  >
                    <BookOpen className="h-3 w-3" /> Continue
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function CertRow({ cert }: { cert: any }) {
  const [busy, setBusy] = useState(false);
  const revoked = !!cert.revoked_at;

  async function handleDownload() {
    setBusy(true);
    try {
      await downloadCertificatePdf({
        verificationCode: cert.verification_code,
        recipientName: cert.recipient_name,
        trackLabel: TRACK_LABEL[cert.track] ?? cert.track,
        title: cert.title,
        issuedAt: cert.issued_at,
        trackRef: cert.track_ref,
        signature: cert.signature,
        certificateId: cert.id,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="rounded-lg border border-input bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gold">
            {TRACK_LABEL[cert.track] ?? cert.track} · {cert.track_ref}
          </p>
          <h3 className="mt-1 font-display text-lg text-navy">{cert.title}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Issued {new Date(cert.issued_at).toLocaleDateString("en-ZA")} ·{" "}
            <span className="font-mono">{cert.verification_code}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {revoked ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
              <ShieldOff className="h-3.5 w-3.5" /> Revoked
            </span>
          ) : (
            <button
              onClick={handleDownload}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-3 py-2 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Download PDF
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
